import { LitElement, html, css } from 'lit';
import tracking from '@finn-no/analytics';
import pulse from '@finn-no/pulse-sdk';
import { i18n } from '@lingui/core';

const converter = (value) => {
    if (!value) return null;

    try {
        const v = JSON.parse(value);
        if (Array.isArray(v)) {
            return v.map((val) => val.value || val);
        }
        if (v.value) {
            return [v.value];
        }
    } catch (err) {
        // noop
    }

    if (typeof value === 'string') {
        return [value];
    }

    return null;
};

class MFPCar extends LitElement {
    static properties = {
        market: { attribute: true, type: String },
        baseurl: { attribute: true, type: String },
        css: { attribute: true, converter },
        js: { attribute: true, converter },
    };

    static styles = css`
        :host {
            display: block;
        }
    `;

    trackNewAd() {
        tracking.trackEventLater({
            analyticsName: 'ny-annonse.click',
            eventKpi: 'effect',
            stringValue: 'car',
        });

        pulse.trackEvent({
            type: 'Click',
            intent: 'Create',
            name: 'New Ad',
            object: {
                id: 'new-ad-btn',
                type: 'UIElement',
                elementType: 'Button',
            },
            target: {
                id: '/innfinn',
                type: 'Page',
                pageType: 'ad_insertion',
            },
        });
    }

    trackEievelger() {
        tracking.trackEventLater({
            analyticsName: 'eievelger.click',
            eventKpi: 'effect',
            stringValue: 'car',
        });

        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: 'Use car chooser guide',
            object: {
                id: 'eievelger-link',
                type: 'UIElement',
                elementType: 'Link',
            },
            target: {
                id: '/eievelger/',
                type: 'Page',
                pageType: 'tool',
            },
        });
    }

    trackPristips() {
        tracking.trackEventLater({
            analyticsName: 'pristips.click',
            eventKpi: 'effect',
            stringValue: 'car',
        });

        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: 'Get pricetip',
            object: {
                id: 'pristips-link',
                type: 'UIElement',
                elementType: 'Link',
            },
            target: {
                id: '/pristips/bruktbil',
                type: 'Page',
                pageType: 'tool',
            },
        });
    }

    trackInsurance() {
        tracking.trackEventLater({
            analyticsName: 'bilforsikring.click',
            eventKpi: 'effect',
            stringValue: 'car',
        });

        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: 'Get price on car insurance',
            object: {
                id: 'insurance-link',
                type: 'UIElement',
                elementType: 'Link',
            },
            target: {
                id: '/okonomi/sjekk/forsikring/bil',
                type: 'Listing',
            },
        });
    }

    trackLoan() {
        tracking.trackEventLater({
            analyticsName: 'billånskalkulator.click',
            eventKpi: 'effect',
            stringValue: 'car',
        });

        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: 'Use car loan calculator',
            object: {
                id: 'loan-link',
                type: 'UIElement',
                elementType: 'Link',
            },
            target: {
                id: '/okonomi/sjekk/lan/bil',
                type: 'Listing',
            },
        });
    }

    trackNetBil() {
        tracking.trackEventLater({
            analyticsName: 'nettbil.click',
            eventKpi: 'effect',
            stringValue: 'car',
        });

        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: 'Sell car with Nettbil',
            object: {
                id: 'nettbil-btn',
                type: 'UIElement',
                elementType: 'Button',
            },
            target: {
                id: '/nettbil',
                type: 'Page',
                pageType: 'product_description',
            },
        });
    }

    trackSalgsAlternativer() {
        tracking.trackEventLater({
            analyticsName: 'salgsalternativer.click',
            eventKpi: 'effect',
            stringValue: 'car',
        });

        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: 'View sales options',
            object: {
                id: 'salgsalternativer-link',
                type: 'UIElement',
                elementType: 'Link',
            },
            target: {
                id: '/salgsalternativer-bil',
                type: 'Page',
            },
        });
    }

    connectedCallback() {
        super.connectedCallback();

        const messages = JSON.parse(this.getAttribute('messages') || '{}');
        i18n.load(this.locale, messages);
        i18n.activate(this.locale);

        pulse.trackPageView({
            object: {
                type: 'MarketFrontpage',
            },
            vertical: {
                // Note: This is at the moment the only exception,
                // where the Pulse vertical is different from the
                // legacy Adobe market/vertical.
                name: 'motor',
            },
        });

        tracking.trackPage({});

        if (navigator.serviceWorker) {
            navigator.serviceWorker.register('/sw.js').catch(() => {});
        }
    }

    render() {
        return html`
            ${this.css.map(
                (style) =>
                    html`<link
                        href="${style}"
                        rel="stylesheet"
                        type="text/css"
                    />`,
            )}
            <a
                href="#market-start"
                class="skiplink absolute top left opacity-0 bg-milk pv-5 ph-10 -z-1"
                >${i18n.t({
                    id: 'car.skiplink',
                    message: 'Go to content',
                    comment:
                        'this messages is shown in a skiplink only shown for users tabbing with a keyboard, clicking it will send focus to the main content of the page',
                })}</a
            >
            <slot name="topbar"></slot>
            <slot name="banner"></slot>
            <main class="page-container pt-16" style="min-height: 60vw">
                <slot name="broadcast"></slot>
                <header class="flex items-center mb-16">
                    <span class="text-aqua-400 mr-8">
                        <slot name="icon"></slot>
                    </span>
                    <h1
                        class="h2 mb-0"
                        data-automation-id="marketTitle"
                        id="market-start"
                    >
                        ${this.market}
                    </h1>
                </header>
                <div class="md:grid f-grid md:grid-cols-3">
                    <div class="md:col-span-2 -ml-16">
                        <slot name="links"></slot>
                        <div class="p-16">
                            <slot name="active-seller"></slot>
                        </div>
                    </div>
                    <div>
                        <section
                            class="group block relative break-words last-child:mb-0 p-16 ml-16 mb-24 rounded-8 bg-blue-50"
                            aria-labelledby="selgKjoeretoey"
                        >
                            <h2 class="h3 mb-16" id="selgKjoeretoey">
                                ${i18n.t({
                                    id: 'car.sell.title',
                                    message: 'Sell vehicle',
                                    comment:
                                        'title used in the sell section on the car market frontpage',
                                })}
                            </h2>
                            <a
                                data-tracking-id="new-ad"
                                @click=${this.trackNewAd}
                                href="${this.baseurl}/innfinn/car"
                                class="button button--cta mb-16 max-w-full w-full"
                                >${i18n.t({
                                    id: 'car.sell.new-ad.link',
                                    message: 'New ad',
                                    comment:
                                        'text used in link shown as a button that leads to new car ad creation',
                                })}</a
                            >
                            <a
                                data-tracking-id="nettbil"
                                @click=${this.trackNetBil}
                                href="${this
                                    .baseurl}/nettbil/velkommen?origin=car_browse"
                                class="button max-w-full w-full mb-24"
                                >${i18n.t({
                                    id: 'car.sell.nettbil.link',
                                    message: 'Sell with Nettbil',
                                    comment: `text used in link shown as a button that leads to Nettbil's car selling service`,
                                })}
                            </a>
                            <a
                                data-tracking-id="salgsalternativer"
                                @click=${this.trackSalgsAlternativer}
                                href="${this.baseurl}/salgsalternativer-bil"
                                class="block text-center mb-16 mx-24"
                                >${i18n.t({
                                    id: 'car.sell.alternatives.link',
                                    message:
                                        'Read more about how to sell a car on FINN',
                                    comment:
                                        'link to more alternatives on how to sell a car on FINN',
                                })}</a
                            >
                        </section>
                        <section
                            class="group block relative break-words last-child:mb-0 p-16 pr-0 rounded-8"
                            aria-labelledby="nyttigeVerktoey"
                        >
                            <h2 class="h3 mb-8" id="nyttigeVerktoey">
                                ${i18n.t({
                                    id: 'car.tools.title',
                                    message: 'Useful tools',
                                    comment:
                                        'title used in the tools section on the car market frontpage',
                                })}
                            </h2>
                            <a
                                data-tracking-id="pristips"
                                @click=${this.trackPristips}
                                href="${this.baseurl}/pristips/bruktbil"
                                class="flex items-center gap-x-8 py-8 text-grey-800"
                            >
                                <span><slot name="coin-icon"></slot></span>
                                <span
                                    >${i18n.t({
                                        id: 'car.tools.value.link',
                                        message: 'Check what your car is worth',
                                        comment:
                                            'link text, links to tips on how to check the value of your car',
                                    })}</span
                                >
                            </a>
                            <a
                                data-tracking-id="insurance"
                                @click=${this.trackInsurance}
                                href="${this
                                    .baseurl}/okonomi/sjekk/forsikring/bil/"
                                class="flex items-center gap-x-8 py-8 text-grey-800"
                            >
                                <span><slot name="price-tag-icon"></slot></span>
                                <span
                                    >${i18n.t({
                                        id: 'car.tools.insurance.link',
                                        message: 'Insure your car',
                                        comment:
                                            'link text, links to info on how to insure your car',
                                    })}</span
                                >
                            </a>
                            <a
                                data-tracking-id="eievelger"
                                @click=${this.trackEievelger}
                                href="/eievelger/"
                                class="flex items-center gap-x-8 py-8 text-grey-800"
                            >
                                <span><slot name="car-guide-icon"></slot></span>
                                <span
                                    >${i18n.t({
                                        id: 'car.tools.own-or-rent.link',
                                        message:
                                            'Should you own or rent your car? Take the test',
                                        comment:
                                            'link text, links to a test on whether you should own or rent your car',
                                    })}</span
                                >
                            </a>
                            <a
                                data-tracking-id="loan"
                                @click=${this.trackLoan}
                                href="${this.baseurl}/okonomi/sjekk/lan/bil/"
                                class="flex items-center gap-x-8 py-8 text-grey-800"
                            >
                                <span
                                    ><slot name="calculator-icon"></slot
                                ></span>
                                <span
                                    >${i18n.t({
                                        id: 'car.tools.loan-calculator',
                                        message: 'Loan calculator for cars',
                                        comment:
                                            'link text, links to a loan calculator for cars',
                                    })}</span
                                >
                            </a>
                        </section>
                        <slot name="user-searches"></slot>
                    </div>
                </div>
                <slot name="recommendations"></slot>
            </main>
            <slot name="footer"></slot>
            ${this.js.map(
                (js) =>
                    html`<script
                        type="module"
                        src="${js}"
                        crossorigin
                        defer
                    ></script>`,
            )}
        `;
    }
}

customElements.define('mfp-car', MFPCar);
